import {
  useState,
  useEffect,
  useRef,
  useCallback,
  useLayoutEffect,
} from "react";
import "./AgentChat.css";
import "./LoadingDots.css";
import { useSelector } from "react-redux";
import { selectUser, selectMergeConnected } from "../../redux/features/appData";
import toast, { Toaster } from "react-hot-toast";
import useWebSocket from "react-use-websocket";
import { useParams } from "react-router-dom";
import {
  apiAISaveAgentResponse,
  apiGetConversationHistoryPaginated,
  apiStartNewThread,
  apiAreMessagesOutOfSync,
} from "../../services/AIService";
import { HumanMessage as HumanMessageComponent } from "./HumanMessage";
import { AIMessage } from "./AIMessage";
import { ToolMessage } from "./ToolMessage";
import { NewThreadMessage } from "./NewThreadMessage";
import { StatusMessage } from "./StatusMessage";
import { StatusMessageType } from "winslow-core";
import { AgentResponseMessage, AIResponseMessage, HumanMessage } from "./types";
import { MessageType, MessageThreadType } from "winslow-core";
import {
  addMessage,
  selectCurrentThread,
  selectEmployees,
  selectMessages,
  selectPersonas,
  selectPrompt,
  setEmployees,
  setMessages,
  setPersonas,
  setPrompt,
  prependMessages,
  setLoadingMore,
  setPaginationData,
  selectHasMoreMessages,
  selectIsLoadingMore,
  selectIsInitialLoading,
  setInitialLoading,
} from "../../redux/features/agent/agentSlice";
import { useDispatch } from "react-redux";
import { apiGetEmployees } from "../../services/EmployeeService";
import { apiGetPersonas } from "../../services/PersonasService";
import CirclePersonaEmployeeSelect from "../common/CirclePersonaEmployeeSelect";
import config from "environment";
import React from "react";
import { AIStreamResponse } from "../../hooks/useAIStream";
import { AIMessageStreaming } from "./AIMessageStreaming";
import { ModalDebugChat } from "./ModalDebugChat";
import ModalBasicConfirm from "../common/ModalBasicConfirm";
import { LoadingPage } from "../common/LoadingPage";
import { DCRouter } from "../app/dynamic-components/DCRouter";
import { DCComponentRouter } from "../app/dynamic-components/DCComponentRouter";

type AgentChatProps = {};

export const AgentChat = ({}: AgentChatProps) => {
  const storeUser = useSelector(selectUser);
  const dispatch = useDispatch();
  const messages = useSelector(selectMessages);
  const currentThread = useSelector(selectCurrentThread);
  const prompt = useSelector(selectPrompt);
  const employees = useSelector(selectEmployees);
  const personas = useSelector(selectPersonas);
  const hasMoreMessages = useSelector(selectHasMoreMessages);
  const isLoadingMore = useSelector(selectIsLoadingMore);
  const isInitialLoading = useSelector(selectIsInitialLoading);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const [currentResponse, setCurrentResponse] =
    useState<AIStreamResponse | null>(null);

  const [currentStatusMessage, setCurrentStatusMessage] =
    useState<StatusMessageType | null>(null);
  const [currentMessageData, setCurrentMessageData] = useState<any>({});
  const [currentUIObject, setCurrentUIObject] = useState<string | null>(null);
  const [currentAsset, setCurrentAsset] = useState<string | null>(null);

  const statusTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const [lastPrompt, setLastPrompt] = useState<string>("");
  const streamingScrollCounterRef = useRef<number>(0);

  const { collectionId } = useParams();

  const mergeConnected = useSelector(selectMergeConnected);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesStartRef = useRef<HTMLDivElement>(null);
  const lastAIMessageRef = useRef<HTMLDivElement>(null);
  const [lastMessageType, setLastMessageType] = useState<string | null>(null);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const historyRef = useRef<HTMLDivElement>(null);

  const { sendJsonMessage, lastJsonMessage } =
    useWebSocket<AgentResponseMessage>(config.wsAiUrl, {
      onOpen: () => console.log("[websocket] opened"),
      onClose: (code: any) => console.log("[websocket] closed: ", code),
      shouldReconnect: (closeEvent) => true,
      onError: (event: WebSocketEventMap["error"]) =>
        console.log("[websocket] error: ", event),
    });

  const [showDebugModal, setShowDebugModal] = useState(false);

  // Modal state for out-of-sync confirmation
  const [showOutOfSyncModal, setShowOutOfSyncModal] = useState(false);

  const openOutOfSyncModal = () => setShowOutOfSyncModal(true);
  const closeOutOfSyncModal = () => setShowOutOfSyncModal(false);

  const refreshMessages = async () => {
    if (!storeUser || !collectionId) {
      console.error("Cannot refresh messages: Missing required data");
      return;
    }

    try {
      console.log("[AgentChat] Refreshing messages...");
      dispatch(setInitialLoading(true));

      // Load fresh messages from the server (same as initial load)
      const conversationResult = await apiGetConversationHistoryPaginated(
        storeUser.id,
        collectionId,
        5, // Same limit as initial load
        0 // Start from beginning
      );

      console.log("Messages refresh result:", conversationResult);

      if (conversationResult) {
        dispatch(setMessages(conversationResult.messages));
        dispatch(
          setPaginationData({
            hasMore: conversationResult.hasMore,
            totalCount: conversationResult.totalCount,
          })
        );
        console.log("[AgentChat] Messages refreshed successfully");
      }
    } catch (error) {
      console.error("Error refreshing messages:", error);
      dispatch(setInitialLoading(false));
      toast.error("Failed to refresh messages");
    }
  };

  const handleRefresh = async () => {
    try {
      // Close the modal first
      closeOutOfSyncModal();

      // Refresh the messages from the server
      await refreshMessages();

      // Restore saved prompt if it exists
      const saved = sessionStorage.getItem("agentChatSavedPrompt");
      if (saved) {
        dispatch(setPrompt(saved));
        sessionStorage.removeItem("agentChatSavedPrompt");
      }

      toast.success("Messages refreshed successfully");
    } catch (error) {
      console.error("Error refreshing messages:", error);
      toast.error("Failed to refresh messages");
    }
  };

  // Immediate scroll function for streaming (no debounce)
  const scrollToBottomImmediate = useCallback(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "auto" });
    }
  }, []);

  // Debounced scroll function for non-streaming scenarios
  const debouncedScrollToBottom = useCallback(
    (() => {
      let timeoutId: NodeJS.Timeout;
      return () => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: "smooth" });

            // Focus on textarea after scrolling
            setTimeout(() => {
              textareaRef.current?.focus();
            }, 100);
          }
        }, 50); // Reduced from 100ms to 50ms debounce
      };
    })(),
    []
  );

  useEffect(() => {
    if (storeUser && collectionId) {
      const fetchData = async () => {
        try {
          dispatch(setInitialLoading(true));

          // First fetch employees and personas in parallel
          if (storeUser.currentWorkspaceId) {
            const [employeesResult, personasResult] = await Promise.all([
              apiGetEmployees(storeUser.currentWorkspaceId),
              apiGetPersonas(storeUser.currentWorkspaceId),
            ]);

            console.log("[AgentChat] employeesResult", employeesResult);
            console.log("[AgentChat] personasResult", personasResult);

            if (employeesResult) {
              dispatch(setEmployees(employeesResult));
            }

            if (personasResult) {
              dispatch(setPersonas(personasResult));
            }
          }

          // Load only the most recent 5 messages initially
          const conversationResult = await apiGetConversationHistoryPaginated(
            storeUser.id,
            collectionId,
            5,
            0
          );

          console.log("Initial conversation load result:", conversationResult);

          if (conversationResult) {
            dispatch(setMessages(conversationResult.messages));
            dispatch(
              setPaginationData({
                hasMore: conversationResult.hasMore,
                totalCount: conversationResult.totalCount,
              })
            );
            console.log(
              "Set initial pagination data - hasMore:",
              conversationResult.hasMore,
              "totalCount:",
              conversationResult.totalCount
            );
          }
        } catch (error) {
          console.error("Error fetching data:", error);
          dispatch(setInitialLoading(false));
        }
      };

      fetchData();
    }

    // Cleanup function to clear messages when component unmounts
    return () => {
      dispatch(setMessages([]));
      dispatch(setInitialLoading(true)); // Reset to initial loading state
      if (statusTimeoutRef.current) {
        clearTimeout(statusTimeoutRef.current);
      }
    };
  }, [storeUser, collectionId, dispatch]);

  useEffect(() => {
    if (lastJsonMessage) {
      console.log("lastJsonMessage", lastJsonMessage);

      const msg = lastJsonMessage as unknown as AIStreamResponse;

      if (msg.stage === "status") {
        // Handle status messages
        const statusMessage: StatusMessageType = {
          id: Date.now(),
          type: "STATUS",
          stage: "status",
          message: msg.message || "",
          content: msg.content || "",
          success: msg.success || true,
          personaIds:
            (msg as any).personaIds || (msg as any).data?.personaIds || [],
          data: (msg as any).data,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };

        // Clear any existing timeout
        if (statusTimeoutRef.current) {
          clearTimeout(statusTimeoutRef.current);
        }

        console.log("statusMessage", statusMessage);
        // Retain personaIds once they've been received - only update if new ones are provided
        if (
          !statusMessage.personaIds ||
          statusMessage.personaIds.length === 0
        ) {
          if (
            currentStatusMessage &&
            currentStatusMessage.personaIds &&
            currentStatusMessage.personaIds.length > 0
          ) {
            statusMessage.personaIds = currentStatusMessage.personaIds;
          }
        }
        setCurrentStatusMessage(statusMessage);
      } else if (msg.stage === "render") {
        console.log("[AgentChat] Render: ", msg);
        setCurrentResponse(msg);
        setCurrentStatusMessage(null); // Clear status message when starting new response
        setIsLoading(false);
        setCurrentUIObject(msg.data?.object || null);
        setCurrentMessageData(msg.data || {});
        streamingScrollCounterRef.current = 0; // Reset scroll counter
      } else if (msg.stage === "display-asset") {
        console.log("[AgentChat] Display Asset: ", msg);
        setCurrentResponse(msg);
        setCurrentStatusMessage(null); // Clear status message when starting new response
        setIsLoading(false);
        setCurrentAsset(msg.data);
        setCurrentMessageData(msg.data || {});
      } else if (msg.stage === "starting") {
        setCurrentResponse(msg);
        setCurrentStatusMessage(null); // Clear status message when starting new response
        setIsLoading(false);
        streamingScrollCounterRef.current = 0; // Reset scroll counter
      } else if (msg.stage === "streaming") {
        setCurrentResponse((prev) => {
          if (!prev) return null;
          return {
            ...prev,
            content: prev.content + msg.content,
          };
        });
        // Scroll frequently for smooth streaming experience - every 2nd update
        streamingScrollCounterRef.current += 1;
        if (streamingScrollCounterRef.current % 2 === 0) {
          // Use immediate scroll during streaming for responsiveness
          scrollToBottomImmediate();
        }
      } else if (msg.stage === "complete") {
        setIsLoading(false);

        // Use the saved message from the database if available, otherwise construct manually
        let aiMessage: AIResponseMessage;

        if (msg.savedMessage) {
          // Use the complete database record
          aiMessage = {
            id: msg.savedMessage.id,
            type: MessageType.AI_MESSAGE,
            sessionId:
              msg.savedMessage.sessionId || currentThread?.sessionId || "",
            threadId: msg.savedMessage.threadId || currentThread?.id || "",
            sources: msg.savedMessage.sources || [],
            personaIds: msg.savedMessage.personaIds || [],
            message: msg.savedMessage.message || {
              id: ["langchain_core", "messages", "AIResponseMessage"],
              lc: 1,
              type: "constructor",
              kwargs: {
                id: "",
                content: msg.content,
                tool_calls: [],
                invalid_tool_calls: [],
                usage_metadata: {
                  input_tokens: 0,
                  total_tokens: 0,
                  output_tokens: 0,
                  input_token_details: { audio: 0, cache_read: 0 },
                  output_token_details: { audio: 0, reasoning: 0 },
                },
                additional_kwargs: {},
                response_metadata: {
                  model_name: "",
                  tokenUsage: {
                    totalTokens: 0,
                    promptTokens: 0,
                    completionTokens: 0,
                  },
                  finish_reason: "",
                },
              },
            },
            createdAt: msg.savedMessage.createdAt || new Date().toISOString(),
            updatedAt: msg.savedMessage.updatedAt || new Date().toISOString(),
            thread: msg.savedMessage.thread ||
              currentThread || {
                collectionId: collectionId!,
                id: "",
                sessionId: "",
                threadType: MessageThreadType.NONE,
                personaId: undefined,
                employeeId: undefined,
              },
          };
        } else {
          // Fallback to manual construction (legacy behavior)
          aiMessage = {
            id: Date.now(),
            type: MessageType.AI_MESSAGE,
            sessionId: currentThread?.sessionId || "",
            threadId: currentThread?.id || "",
            sources: currentResponse?.sources || [],
            personaIds: currentResponse?.personaIds || [],
            message: {
              id: ["langchain_core", "messages", "AIResponseMessage"],
              lc: 1,
              type: "constructor",
              kwargs: {
                id: "",
                content: msg.content,
                tool_calls: [],
                invalid_tool_calls: [],
                usage_metadata: {
                  input_tokens: 0,
                  total_tokens: 0,
                  output_tokens: 0,
                  input_token_details: { audio: 0, cache_read: 0 },
                  output_token_details: { audio: 0, reasoning: 0 },
                },
                additional_kwargs: {},
                response_metadata: {
                  model_name: "",
                  tokenUsage: {
                    totalTokens: 0,
                    promptTokens: 0,
                    completionTokens: 0,
                  },
                  finish_reason: "",
                },
              },
            },
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            thread: currentThread || {
              collectionId: collectionId!,
              id: "",
              sessionId: "",
              threadType: MessageThreadType.NONE,
              personaId: undefined,
              employeeId: undefined,
            },
          };
        }

        dispatch(addMessage(aiMessage));
        setLastMessageType(MessageType.AI_MESSAGE);
        setIsLoading(false);
        setCurrentResponse(null);
        setCurrentStatusMessage(null); // Clear status message when response is complete

        // Immediate scroll to bottom to show final formatted result
        scrollToBottomImmediate();

        // Follow up with smooth scroll after content settles
        setTimeout(() => {
          debouncedScrollToBottom();
        }, 100);

        if (msg.savedMessage && msg.savedMessage.id) {
          apiAISaveAgentResponse({
            question: lastPrompt,
            response: msg.content,
            userId: storeUser!.id,
            originatorEmail: undefined,
            originatorName: undefined,
            workspaceId: storeUser!.currentWorkspaceId!,
            collectionId: collectionId!,
            linkRecordIds: msg.sources || [],
            feedback: undefined,
            sources: msg.sources || [],
            messageId: msg.savedMessage.id,
            employeeId: currentThread?.employeeId || null,
          });
        }
      } else {
        console.info("Unknown message stage:", msg);
      }
    }
  }, [lastJsonMessage]);

  // Only scroll to bottom when we should (new messages, not pagination)
  // useEffect(() => {
  //   if (shouldScrollToBottom && !isLoadingMore) {
  //     scrollToBottom();
  //   }
  // }, [messages, shouldScrollToBottom, isLoadingMore]);

  // Scroll when loading state changes (for new messages)
  useEffect(() => {
    if (isLoading) {
      scrollToBottom();
    }
  }, [isLoading]);

  // Use layoutEffect for immediate visual updates during streaming
  useLayoutEffect(() => {
    if (currentResponse && currentResponse.content) {
      // Trigger immediate visual update during active streaming
      // This ensures the DOM is updated before the browser paints
    }
  }, [currentResponse?.content]);

  // Function to scroll to the bottom of the chat (for immediate scrolling)
  const scrollToBottom = () => {
    setTimeout(() => {
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({ behavior: "smooth" });

        // Focus on textarea after scrolling
        setTimeout(() => {
          textareaRef.current?.focus();
        }, 100);
      }
    }, 150); // Reduced from 300ms to 150ms for faster response
  };

  // Function to load more messages when scrolling to top
  const loadMoreMessages = async () => {
    if (!storeUser || !collectionId || isLoadingMore || !hasMoreMessages) {
      console.log("loadMoreMessages early return:", {
        hasUser: !!storeUser,
        hasCollectionId: !!collectionId,
        isLoadingMore,
        hasMoreMessages,
      });
      return;
    }

    console.log("Loading more messages - current count:", messages.length);
    dispatch(setLoadingMore(true));

    // Store current scroll position
    const scrollContainer = historyRef.current;
    const previousScrollHeight = scrollContainer?.scrollHeight || 0;

    try {
      const result = await apiGetConversationHistoryPaginated(
        storeUser.id,
        collectionId,
        5,
        messages.length
      );

      console.log("Pagination result:", result);

      if (result && result.messages.length > 0) {
        console.log("Adding", result.messages.length, "more messages");
        dispatch(
          prependMessages({
            messages: result.messages,
            hasMore: result.hasMore,
            totalCount: result.totalCount,
          })
        );

        // Restore scroll position after new messages are loaded
        setTimeout(() => {
          if (scrollContainer) {
            const newScrollHeight = scrollContainer.scrollHeight;
            const scrollDifference = newScrollHeight - previousScrollHeight;
            console.log("Scroll restoration - difference:", scrollDifference);
            scrollContainer.scrollTop = scrollDifference;
          }
          dispatch(setLoadingMore(false));
        }, 100);
      } else {
        // No more messages to load
        console.log("No more messages to load");
        dispatch(setLoadingMore(false));
      }
    } catch (error) {
      console.error("Error loading more messages:", error);
      dispatch(setLoadingMore(false));
    }
  };

  // Handle scroll events to detect when user scrolls to top
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;

    // If user scrolled to near the top (within 100px) and we have more messages
    if (scrollTop < 100 && hasMoreMessages && !isLoadingMore) {
      console.log(
        "Triggering loadMoreMessages - scrollTop:",
        scrollTop,
        "hasMoreMessages:",
        hasMoreMessages,
        "isLoadingMore:",
        isLoadingMore
      );
      loadMoreMessages();
    }
  };

  useEffect(() => {
    if (currentThread) {
      console.log("currentThread", currentThread);
      // Scroll when thread changes
      scrollToBottom();
    }
  }, [currentThread]);

  const handleSendMessage = async () => {

    console.log("[AgentChat] handleSendMessage collectionId:", collectionId);
    console.log("[AgentChat] handleSendMessage workspaceId:", storeUser?.currentWorkspaceId);

    if (!prompt.trim() || !storeUser || !collectionId) {
      if (!storeUser) toast("No user available");
      if (!collectionId) toast("No collection selected");
      return;
    }

    // Check for out-of-sync messages before we send
    try {
      const lastMessage = messages[messages.length - 1];
      const lastMessageId = lastMessage ? Number(lastMessage.id) : 0;
      const sessionId = currentThread?.sessionId || "";

      const outOfSync = await apiAreMessagesOutOfSync(lastMessageId, sessionId);

      if (outOfSync) {
        openOutOfSyncModal();
        return; // stop normal flow
      }
    } catch (err) {
      console.error("Error checking message sync", err);
      // allow normal flow if error
    }

    setLastPrompt(prompt);

    // Create a human message to add to the UI
    const newHumanMessage: HumanMessage = {
      id: Date.now(),
      type: MessageType.USER_MESSAGE,
      sessionId: "",
      threadId: currentThread?.id || "",
      message: {
        id: ["langchain_core", "messages", "HumanMessage"],
        lc: 1,
        type: "constructor",
        kwargs: {
          content: prompt,
          additional_kwargs: {},
          response_metadata: {},
        },
      },
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      thread: currentThread || {
        collectionId: collectionId!,
        id: "",
        sessionId: "",
        threadType: MessageThreadType.NONE,
        personaId: "",
        employeeId: "",
      },
    };

    dispatch(addMessage(newHumanMessage));
    setLastMessageType(MessageType.USER_MESSAGE);
    dispatch(setPrompt(""));
    setIsLoading(true);
    setCurrentStatusMessage(null); // Clear any previous status message

    // Immediate visual feedback - scroll immediately then smooth follow-up
    scrollToBottomImmediate();
    setTimeout(() => {
      scrollToBottom();
    }, 50);

    sendJsonMessage({
      userId: storeUser.id,
      action: "ask",
      inputMessage: prompt,
      collectionId: collectionId,
      employeeId: currentThread?.employeeId,
      personaId: currentThread?.personaId,
      threadId: currentThread?.id,
      workspaceId: storeUser.currentWorkspaceId,
    });
  };

  // Restore prompt after refresh if we previously saved it
  useEffect(() => {
    const saved = sessionStorage.getItem("agentChatSavedPrompt");
    if (saved) {
      dispatch(setPrompt(saved));
      sessionStorage.removeItem("agentChatSavedPrompt");
    }
  }, [dispatch, setPrompt]);

  const handleExpand = (message: AgentResponseMessage) => {
    // Logic for expanding a message
    console.log("Message expanded:", message.id);
  };

  const handleStartNewThread = async (
    personaId: string | null,
    employeeId: string | null
  ) => {
    if (!storeUser || !collectionId) {
      if (!storeUser) toast("No user available");
      if (!collectionId) toast("No collection selected");
      return;
    }

    // Guard against out-of-sync client state before creating a new thread
    try {
      const lastMessage = messages[messages.length - 1];
      const lastMessageId = lastMessage ? Number(lastMessage.id) : 0;
      const sessionId = currentThread?.sessionId || "";

      const outOfSync = await apiAreMessagesOutOfSync(lastMessageId, sessionId);

      if (outOfSync) {
        sessionStorage.setItem("agentChatSavedPrompt", prompt);
        openOutOfSyncModal();
        return; // Abort thread creation
      }
    } catch (err) {
      console.error("Error checking message sync", err);
      // If the check fails, continue with normal behaviour
    }

    const newThreadMessage = await apiStartNewThread(
      storeUser.id,
      collectionId,
      personaId,
      employeeId
    );

    console.log("newThreadMessage", newThreadMessage);

    if (!newThreadMessage) {
      toast("Failed to start new thread");
      return;
    }

    dispatch(addMessage(newThreadMessage));
    //scroll to bottom
    scrollToBottom();
    setLastMessageType(MessageType.NEW_THREAD);
  };

  console.log("[AgentChat] isLoading: ", isLoading);
  console.log("[AgentChat] messages lengths: ", messages.length);
  dispatch(setInitialLoading(false));

  return (
    <div className="agent-chat">
      {config.showDebugChat && (
        <button
          className="agent-chat-debug-button"
          onClick={() => setShowDebugModal(true)}>
          Debug Chat
        </button>
      )}

      <div className="agent-chat-content">
        <div className="agent-chat-messages">
          <div
            ref={historyRef}
            className="agent-chat-history-items"
            onScroll={handleScroll}
            style={{ maxHeight: "100%", overflowY: "auto" }}>
            {/* Show LoadingPage for initial loading */}
            {isInitialLoading ? (
              <div className="agent-chat-zero-state">
                <div className="agent-chat-zero-state-content">
                  <LoadingPage
                    loadingMessage="Loading..."
                    className="agent-chat-zero-state-loading-page"
                    disableAnimation={false}
                  />
                </div>
              </div>
            ) : (
              <>
                {messages.length === 0 && !isLoading ? (
                  <div className="agent-chat-zero-state">
                    <div className="agent-chat-zero-state-content">
                      <LoadingPage
                        loadingMessage=""
                        className="agent-chat-zero-state-loading-page"
                        disableAnimation={true}
                      />
                      <div className="agent-chat-zero-state-text">
                        <div className="agent-chat-zero-state-title">
                          No messages yet
                        </div>
                        <div className="agent-chat-zero-state-description">
                          Start a conversation by typing a message below.
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    <div ref={messagesStartRef} />
                    {isLoadingMore && (
                      <div className="agent-chat-loading-more">
                        <div className="loading-dots">
                          <div className="loading-dot" />
                          <div className="loading-dot" />
                          <div className="loading-dot" />
                        </div>
                        <span>Loading more messages...</span>
                      </div>
                    )}
                    {messages.map((message, index) => {
                      // Skip rendering if this is the first message and it's a NEW_THREAD message
                      // if (index === 0 && message.type === MessageType.NEW_THREAD) {
                      //   return null;
                      // }

                      return (
                        <React.Fragment key={message.id.toString()}>
                          {message.type === MessageType.USER_MESSAGE && (
                            <HumanMessageComponent
                              message={message}
                              handleStartNewThread={handleStartNewThread}
                            />
                          )}
                          {message.type === MessageType.AI_MESSAGE && (
                            <AIMessage
                              message={message}
                              onExpand={handleExpand}
                            />
                          )}
                          {message.type === MessageType.TOOL_CALL && (
                            <ToolMessage
                              toolType={
                                message.message.kwargs.tool_calls[0].name
                              }
                            />
                          )}
                          {message.type === MessageType.NEW_THREAD && (
                            <>
                              <NewThreadMessage message={message} />
                            </>
                          )}
                        </React.Fragment>
                      );
                    })}
                    {/* Render current status message */}
                    {currentStatusMessage && (
                      <StatusMessage
                        key={currentStatusMessage.id}
                        message={currentStatusMessage.message}
                        personaIds={currentStatusMessage.personaIds}
                        data={currentStatusMessage.data}
                        success={currentStatusMessage.success}
                      />
                    )}
                    {currentUIObject && 
                      <DCComponentRouter
                        objectName={currentUIObject}
                        data={currentMessageData}
                      />
                    }
                    {currentAsset && (
                      <DCComponentRouter
                        objectName={"asset"}
                        data={currentMessageData}
                      />
                    )}
                    {isLoading ? (
                      <div
                        className="agent-chat-history-item"
                        style={{ minHeight: "24px" }}>
                        <div className="loading-dots">
                          <div className="loading-dot" />
                          <div className="loading-dot" />
                          <div className="loading-dot" />
                        </div>
                      </div>
                    ) : (
                      <div
                        className="agent-chat-history-item"
                        style={{ minHeight: "24px" }}></div>
                    )}
                    {currentResponse && (
                      <AIMessageStreaming
                        sources={currentResponse.sources}
                        personaIds={currentResponse.personaIds}
                        content={currentResponse.content}
                      />
                    )}
                  </>
                )}
              </>
            )}
            <div ref={messagesEndRef} />
          </div>
        </div>

        <div className="agent-chat-input">
          <div className="agent-chat-input-row">
            <textarea
              ref={textareaRef}
              value={prompt}
              onChange={(e) => dispatch(setPrompt(e.target.value))}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSendMessage();
                }
              }}
              placeholder="Type your message..."
              disabled={isLoading || isInitialLoading}
              rows={1}
              onInput={(e) => {
                const target = e.target as HTMLTextAreaElement;
                target.style.height = "auto";
                const newHeight = Math.min(target.scrollHeight, 120);
                target.style.height = `${newHeight}px`;
              }}
            />
            <button
              onClick={handleSendMessage}
              disabled={isLoading || isInitialLoading || !prompt.trim()}>
              Send
            </button>
          </div>
          {config.showPersonas && (
            <div className="agent-chat-persona-selector">
              <CirclePersonaEmployeeSelect
                disabled={!mergeConnected || isInitialLoading}
                employees={employees}
                personas={personas}
                onChange={(value, isPersona) => {
                  // Check if the selected value is the same as current selection
                  const currentPersonaId = currentThread?.personaId;
                  const currentEmployeeId = currentThread?.employeeId;

                  if (value === "" || value === null) {
                    // Only create new thread if we're not already in a "no filter" state
                    if (currentPersonaId || currentEmployeeId) {
                      handleStartNewThread(null, null);
                    }
                    return;
                  }

                  if (isPersona) {
                    // Only start new thread if different persona is selected
                    if (value !== currentPersonaId) {
                      handleStartNewThread(value, null);
                    }
                  } else {
                    // Only start new thread if different employee is selected
                    if (value !== currentEmployeeId) {
                      handleStartNewThread(null, value);
                    }
                  }
                }}
                selectedEmployee={currentThread?.employeeId}
                selectedPersona={currentThread?.personaId}
                includeNoFilter={true}
              />
            </div>
          )}
        </div>
      </div>

      <ModalDebugChat
        isOpen={showDebugModal}
        onClose={() => setShowDebugModal(false)}
      />

      {/* Out-of-sync confirmation modal */}
      <ModalBasicConfirm
        isOpen={showOutOfSyncModal}
        onClose={closeOutOfSyncModal}
        title="Messages Updated"
        message="New messages have arrived in this conversation. You need to refresh."
        onContinue={handleRefresh}
        continueLabel="Refresh Messages"
        allowDismiss={false}
        allowClickout={false}
      />

      <Toaster />
    </div>
  );
};
